import {
  BROADCAST_MESSAGE_WRAPPER_TYPE,
  BroadcastEnvelope,
  NotificationItem,
  isNotificationDomainEvent,
} from "../types";

type NormalizedBase = {
  id: string;
  type?: string;
  occurred_at?: string;
  created_at?: string;
  payload?: Record<string, unknown>;
  data?: {
    event?: string;
    payload?: Record<string, unknown>;
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const toRecord = (value: unknown): Record<string, unknown> =>
  isRecord(value) ? value : {};

const parseMaybeJson = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
};

const toEnvelopeShape = (value: unknown): NormalizedBase | null => {
  const parsed = parseMaybeJson(value);
  if (!isRecord(parsed)) {
    return null;
  }

  if (isRecord(parsed.notification)) {
    return parsed.notification as NormalizedBase;
  }

  return parsed as NormalizedBase;
};

export const normalizeIncomingEnvelope = (
  incoming: unknown,
  wireEventName?: string
): Omit<NotificationItem, "isRead"> | null => {
  const envelope = toEnvelopeShape(incoming);
  if (!envelope || typeof envelope.id !== "string") {
    return null;
  }

  const dataEvent =
    wireEventName ??
    (typeof envelope.data?.event === "string" ? envelope.data.event : undefined);

  const candidateType =
    envelope.type === BROADCAST_MESSAGE_WRAPPER_TYPE
      ? dataEvent
      : envelope.type && isNotificationDomainEvent(envelope.type)
        ? envelope.type
        : dataEvent ?? envelope.type;

  if (!candidateType || !isNotificationDomainEvent(candidateType)) {
    return null;
  }

  const occurredAt = envelope.occurred_at || envelope.created_at;
  if (!occurredAt) {
    return null;
  }

  const payload = envelope.payload ?? envelope.data?.payload ?? envelope.data ?? {};

  return {
    id: envelope.id,
    eventName: candidateType,
    occurredAt,
    payload: toRecord(payload),
  };
};

export const normalizeEnvelopeFromApiRecord = (
  row: Record<string, unknown>
): Omit<NotificationItem, "isRead"> | null => {
  const base: BroadcastEnvelope<unknown> = {
    id: String(row.id ?? ""),
    type: typeof row.type === "string" ? row.type : undefined,
    created_at: typeof row.created_at === "string" ? row.created_at : undefined,
    occurred_at:
      typeof row.occurred_at === "string" ? row.occurred_at : undefined,
    payload: isRecord(row.payload) ? row.payload : undefined,
    data: isRecord(row.data)
      ? {
          event: typeof row.data.event === "string" ? row.data.event : undefined,
          payload: isRecord(row.data.payload) ? row.data.payload : row.data,
        }
      : undefined,
  };

  const wireEventName =
    isRecord(row.data) && typeof row.data.event === "string"
      ? row.data.event
      : undefined;

  return normalizeIncomingEnvelope(base, wireEventName);
};
